import { Chip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import RemoveIcon from "@mui/icons-material/Remove";

// Report Status / Qualified By Brands both render as an outlined, icon-led
// chip — in LeadsTable.tsx's columns and in LeadDetailDrawer.tsx's header.
// Their meta comes from different sources (STATUS_META keyed by LeadStatus
// vs getQualifiedMeta's boolean|null), but both settle on the same small
// set of display labels, so the icon/color is looked up by that label
// rather than duplicating this per source.
//
// Three independent colors per status (not one color + alpha()) — bg is a
// pale tint, border a mid-tone of the same hue, text/icon the darkest shade,
// matching the target chip design (pale green fill / mid-green border /
// dark-green text, same pattern per color family).
const STATUS_ICON_META: Record<
  string,
  {
    icon: typeof CheckCircleOutlineIcon;
    color: string;
    borderColor: string;
    bg: string;
  }
> = {
  Success: { icon: CheckCircleOutlineIcon, color: "#1E4620", borderColor: "#2E7D3280", bg: "#EDF7ED" },
  Qualified: { icon: CheckCircleOutlineIcon, color: "#1E4620", borderColor: "#2E7D3280", bg: "#EDF7ED" },
  Failure: { icon: CancelOutlinedIcon, color: "#5F2120", borderColor: "#D32F2F80", bg: "#FDEDED" },
  "Not Qualified": { icon: CancelOutlinedIcon, color: "#5F2120", borderColor: "#D32F2F80", bg: "#FDEDED" },
  Rejected: { icon: CancelOutlinedIcon, color: "#5F2120", borderColor: "#D32F2F80", bg: "#FDEDED" },
  "Duplicate IP": { icon: ErrorOutlineIcon, color: "#663C00", borderColor: "#ED6C0280", bg: "#FFF4E5" },
  "Brand Duplication": { icon: ErrorOutlineIcon, color: "#663C00", borderColor: "#ED6C0280", bg: "#FFF4E5" },
  "Duplicate Email": { icon: ErrorOutlineIcon, color: "#663C00", borderColor: "#ED6C0280", bg: "#FFF4E5" },
  Pending: { icon: PendingOutlinedIcon, color: "#014361", borderColor: "#0288D180", bg: "#E5F6FD" },
  Initial: { icon: RemoveIcon, color: "#00000099", borderColor: "#9E9E9E", bg: "#E0E0E0" },
  "Not Supported": { icon: RemoveIcon, color: "#00000099", borderColor: "#9E9E9E", bg: "#E0E0E0" },
  Logged: { icon: RemoveIcon, color: "#00000099", borderColor: "#9E9E9E", bg: "#E0E0E0" },
};

const DESKTOP_LABEL_SX = {
  height: "25px",
  "& .MuiChip-label": {
    pl: "6px",
    pr: "4px",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "13px",
    lineHeight: "143%",
    letterSpacing: "0.17px",
  },
};

// The brand an attempt was reported to, shown beside the attempt's status
// chip in the Report Log (desktop and mobile). Same outlined chip shape as
// StatusChip, in the brand purple so it reads as a label, not a status.
export function ReportLogBrandChip({ brand, desktopLabel = true }: { brand: string; desktopLabel?: boolean }) {
  if (!brand) return null;
  return (
    <Chip
      label={brand}
      size="small"
      variant="outlined"
      sx={{
        color: "#7B1FA2",
        borderColor: "#9C27B080",
        bgcolor: "#F3E5F5",
        borderRadius: 1,
        ...(desktopLabel ? DESKTOP_LABEL_SX : { fontWeight: 500 }),
      }}
    />
  );
}

// LeadsMobileStatusChip falls back to this chip for statuses it doesn't
// restyle; mobile passes desktopLabel={false} to keep MUI's default label
// font/padding there, since the Roboto label spec is desktop-only.
export default function StatusChip({ label, desktopLabel = true }: { label: string; desktopLabel?: boolean }) {
  const meta = STATUS_ICON_META[label];
  const labelSx = desktopLabel ? DESKTOP_LABEL_SX : {};
  if (!meta) return <Chip label={label} size="small" variant="outlined" sx={labelSx} />;
  const Icon = meta.icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: "16px !important", color: `${meta.color} !important` }} />}
      label={label}
      size="small"
      variant="outlined"
      sx={{
        color: meta.color,
        borderColor: meta.borderColor,
        bgcolor: meta.bg,
        borderRadius: 1,
        ...(desktopLabel ? {} : { fontWeight: 500 }),
        ...labelSx,
      }}
    />
  );
}
